import React from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { ChartCard } from '@/components/chart-card';
import i18next from '@/i18next';
import { formatChartDateLabel } from '@/helpers/chart-label.helper';
const chartConfig = {
    count: {
        label: i18next.t('scans'),
        color: 'var(--chart-1)',
    },
};
export function ActivityChart({ timeline }) {
    const { t } = useTranslation('glossary');
    const totals = React.useMemo(() => {
        const total = timeline.reduce((acc, p) => acc + p.count, 0);
        const half = Math.floor(timeline.length / 2);
        const previous = timeline.slice(0, half).reduce((acc, p) => acc + p.count, 0);
        const current = timeline.slice(half).reduce((acc, p) => acc + p.count, 0);
        const delta = previous === 0 ? (current > 0 ? 100 : 0) : ((current - previous) / previous) * 100;
        return { total, delta };
    }, [timeline]);
    const isEmpty = timeline.length === 0 || totals.total === 0;
    return (<ChartCard title={t('dashboard.activity.title')} value={totals.total.toLocaleString()} delta={timeline.length > 1
            ? { value: totals.delta, label: t('vs_previous_period', { defaultValue: 'vs. previous period' }) }
            : undefined} isEmpty={isEmpty} empty={t('dashboard.activity.empty')} contentHeightClassName="h-80">
            <ChartContainer config={chartConfig} className="h-full w-full">
                <AreaChart accessibilityLayer data={timeline} margin={{ left: 8, right: 12, top: 16, bottom: 8 }}>
                    <defs>
                        <linearGradient id="activity-fill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--color-count)" stopOpacity={0.35}/>
                            <stop offset="100%" stopColor="var(--color-count)" stopOpacity={0}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60"/>
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}/>
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={36} tickMargin={8} tickFormatter={formatCompactNumber} domain={[0, (dataMax) => Math.max(1, Math.ceil(dataMax * 1.25))]}/>
                    <ChartTooltip cursor={{ strokeDasharray: '3 3' }} content={<ChartTooltipContent indicator="dot" nameKey="count" labelFormatter={formatChartDateLabel}/>}/>
                    <Area type="monotone" dataKey="count" stroke="var(--color-count)" strokeWidth={2} fill="url(#activity-fill)" activeDot={{ r: 4, strokeWidth: 2, className: 'fill-background' }}/>
                </AreaChart>
            </ChartContainer>
        </ChartCard>);
}
function formatCompactNumber(value) {
    if (value >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (value >= 1_000) {
        return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return String(value);
}
